

















import Vue from 'vue';
import {Component, Prop} from "vue-property-decorator";
import {Icon} from "@/helpers/icons";
import ReportModel from "@/models/report/Report";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
import AsiTabs from "@/components/common/AsiTabs";
import ReportTabDetails from "@/components/report/ReportTabDetails.vue";
import AsiPageTitle from "@/components/common/AsiPageTitle.vue";
import AsiMenuItem from "@/components/common/AsiMenuItem.vue";
import StateBar from "@/components/StateBar.vue";
import {Permissions, ReportState} from "@/helpers/constants";
import UserBlamable from "@/models/user/UserBlamable";

@Component({
	components: {StateBar, AsiConfirmDialog, AsiMenuItem, AsiPageTitle, ReportTabDetails, AsiTabs}
})
export default class Report extends Vue {

	@Prop({type: String, required: true})
	public id!: string;

	private icons = Icon;
	private loading: boolean = false;
	private report: ReportModel | null = null;

	private get title(): string {
		if (this.report === null) return '';
		return [
			this.report.order.company.name,
			this.report.order.name,
		].join(' - ');
	}

	private get subtitle(): string {
		if (this.report === null) return '';
		return this.$t('uiTerms.userAtDate', {
			user: UserBlamable.fullName(this.report.user),
			date: this.$d(new Date(this.report.date), 'short'),
		}).toString();
	}

	private get isReportCreated(): boolean {
		return this.report !== null && this.report.state === ReportState.created;
	}

	private get isReportChecked(): boolean {
		return this.report !== null && this.report.state === ReportState.checked;
	}

	private get canReportBeChecked(): boolean {
		if (this.report === null) return false;
		if (!this.isCheckReports) return false;

		const emptyLine = this.report.reportLines.find(rl => rl.amount === null) ?? null;
		return emptyLine === null;
	}

	private get canReportBeUnchecked(): boolean {
		if (this.report === null) return false;
		return this.isCheckReports && this.report.state === ReportState.checked;

	}

	private get canReportBeDeleted(): boolean {
		if (this.report === null) return false;
		return this.isOffice && this.report.state === ReportState.created;
	}

	private get isAdmin(): boolean {
		return this.$store.getters['user/isAdmin'];
	}

	private get isCheckReports(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightCheckReports);
	}

	private get isOffice(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightOffice);
	}

	public mounted(): void {
		this.loadReport();
	}

	private loadReport(): void {
		this.loading = true;
		this.$reportService.report(this.id)
			.then(report => {
				this.report = report;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loading = false;
			});
	}

	private deleteReport(): void {
		if (this.report === null) return;

		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.report === null) return;

			this.loading = true;
			this.$reportService.delete(this.report.id)
				.then(() => this.$router.push({name: 'Reports'}))
				.catch(() => {
					this.$store.commit('ui/showSnackbar', {
						message: this.$t('snackbar.deleteError'),
						color: 'error',
					} as SnackbarMessage);
				})
				.finally(() => this.loading = false);
		});
	}

	private checkReport(): void {
		this.setReportState(ReportState.checked);
	}

	private uncheckReport(): void {
		this.setReportState(ReportState.created);
	}

	private setReportState(state: ReportState): void {
		const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
		confirm.openDialog().then((res: boolean) => {
			if (!res || this.report === null) return;

			this.loading = true;
			this.$reportService.state(this.report.id, state)
				.then(() => this.loadReport())
				.catch(() => this.updateError())
				.finally(() => this.loading = false);
		});
	}

	private updateError(): void {
		this.$store.commit('ui/showSnackbar', {
			message: this.$t('snackbar.updateError'),
			color: 'error',
		} as SnackbarMessage);
	}

}
